import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'

function fmt(n) {
  n = Math.round(n)
  if (n >= 10000000) return '₹' + (n / 10000000).toFixed(1) + ' Cr'
  if (n >= 100000) return '₹' + (n / 100000).toFixed(1) + ' L'
  if (n >= 1000) return '₹' + n.toLocaleString('en-IN')
  return '₹' + n
}

function pct(r) { return parseFloat(r).toFixed(2) + '%' }

// ── HEALTH SCORE ─────────────────────────────────────────────
function calcHealthScore(loans, emiPayments, currentMonth) {
  if (!loans.length) return null
  let score = 100

  loans.forEach(l => {
    const rate = parseFloat(l.interest_rate)
    if (rate > 20) score -= 20
    else if (rate > 10) score -= 10
    else if (rate > 7) score -= 5
  })

  const tBal = loans.reduce((s, l) => s + parseFloat(l.balance), 0)
  const tOri = loans.reduce((s, l) => s + parseFloat(l.original_amount), 0)
  score += (1 - tBal / (tOri || 1)) * 20

  const ccDebt = loans.filter(l => l.type === 'credit').reduce((s, l) => s + parseFloat(l.balance), 0)
  if (ccDebt > 50000) score -= 15
  else if (ccDebt > 20000) score -= 8
  else if (ccDebt > 0) score -= 4

  const thisMonth = emiPayments.filter(p => p.payment_month === currentMonth)
  const paid = thisMonth.filter(p => p.status === 'paid').length
  const applicable = loans.filter(l => {
    const p = thisMonth.find(p => p.loan_id === l.id)
    return !p || p.status !== 'na'
  }).length
  if (applicable > 0) score += (paid / applicable) * 10

  return Math.max(10, Math.min(100, Math.round(score)))
}

// ── SUGGESTIONS ──────────────────────────────────────────────
function getSuggestions(loans, emiPayments, currentMonth) {
  if (!loans.length) return []
  const suggestions = []
  const thisMonth = emiPayments.filter(p => p.payment_month === currentMonth)

  const unpaid = loans.filter(l => thisMonth.find(p => p.loan_id === l.id && p.status === 'unpaid'))
  if (unpaid.length) {
    suggestions.push({
      icon: '⚠️', cls: 'alert',
      title: `${unpaid.length} EMI${unpaid.length > 1 ? 's' : ''} unpaid this month`,
      text: `Pay ${unpaid.map(l => l.name).join(', ')} to avoid late fees and credit score damage.`
    })
  }

  const cc = loans.filter(l => l.type === 'credit' && parseFloat(l.interest_rate) > 15)
    .sort((a, b) => b.interest_rate - a.interest_rate)
  if (cc.length) {
    suggestions.push({
      icon: '🔥', cls: 'alert',
      title: `High-rate credit card: ${cc[0].name}`,
      text: `${pct(cc[0].interest_rate)} p.a. Consider a balance transfer to save ${fmt(cc[0].balance * cc[0].interest_rate / 100)} in annual interest.`
    })
  }

  const sr = [...loans].sort((a, b) => b.interest_rate - a.interest_rate)
  if (sr.length >= 2) {
    suggestions.push({
      icon: '⚡', cls: 'warn',
      title: 'Avalanche strategy recommended',
      text: `Pay extra towards ${sr[0].name} (${pct(sr[0].interest_rate)}) to minimise total interest paid.`
    })
  }

  const lr = loans.filter(l => parseFloat(l.interest_rate) < 6)
  if (lr.length) {
    suggestions.push({
      icon: '📈', cls: 'good',
      title: 'Consider SIP vs. prepaying',
      text: `${lr[0].name} is at ${pct(lr[0].interest_rate)}. Index fund SIPs may yield better long-term returns.`
    })
  }

  const ad = loans.filter(l => (1 - l.balance / l.original_amount) > 0.8)
  if (ad.length) {
    suggestions.push({
      icon: '🎯', cls: 'good',
      title: `Almost done with ${ad[0].name}!`,
      text: `Over 80% paid off! A small extra payment each month can close it sooner.`
    })
  }

  if (!suggestions.length) {
    suggestions.push({
      icon: '✅', cls: 'good',
      title: 'Great financial position!',
      text: 'Keep up consistent EMI payments and invest any surplus to build wealth.'
    })
  }

  return suggestions.slice(0, 5)
}

const clsColors = {
  alert: 'rgba(184,68,26,0.1)',
  warn: 'rgba(201,168,76,0.15)',
  good: 'rgba(74,103,65,0.12)',
  info: 'rgba(61,74,92,0.12)'
}

export default function HealthAndSuggestions({ loans, emiPayments, currentMonth, isPaid, onFeatureLock }) {
  const [sponsors, setSponsors] = useState([])
  const [messages, setMessages] = useState([])
  const [dismissedMessages, setDismissedMessages] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dc_dismissed_msgs') || '[]') } catch { return [] }
  })

  useEffect(() => {
    fetchSponsors()
    fetchMessages()
  }, [])

  async function fetchSponsors() {
    const { data } = await supabase
      .from('sponsor_partners')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true })
    if (data) setSponsors(data)
  }

  async function fetchMessages() {
    const { data } = await supabase
      .from('admin_messages')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false })
    if (data) setMessages(data)
  }

  function dismissMessage(id) {
    const updated = [...dismissedMessages, id]
    setDismissedMessages(updated)
    localStorage.setItem('dc_dismissed_msgs', JSON.stringify(updated))
  }

  const score = calcHealthScore(loans, emiPayments, currentMonth)
  const suggestions = getSuggestions(loans, emiPayments, currentMonth)
  const visibleMessages = messages.filter(m => !dismissedMessages.includes(m.id))

  const panelStyle = {
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: '8px', overflow: 'hidden', marginBottom: '1.5rem'
  }
  const hdrStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '1rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)'
  }

  return (
    <div>

      {/* ANNOUNCEMENTS */}
      {visibleMessages.map(msg => (
        <div key={msg.id} style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: '0.75rem', padding: '0.85rem 1.2rem',
          background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)',
          borderRadius: '8px', marginBottom: '1rem',
          borderLeft: '3px solid #c9a84c'
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '600', fontSize: '0.85rem', marginBottom: '0.2rem' }}>
              📢 {msg.title}
            </div>
            <div style={{ fontSize: '0.78rem', color: '#3d4a5c', lineHeight: 1.5 }}>
              {msg.message}
            </div>
          </div>
          <button
            onClick={() => dismissMessage(msg.id)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '1rem', flexShrink: 0 }}
          >✕</button>
        </div>
      ))}

      {/* HEALTH SCORE */}
      <div style={{ background: '#0d0d0d', color: '#f5f0e8', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', right: '-40px', bottom: '-40px', width: '150px', height: '150px', borderRadius: '50%', background: 'rgba(201,168,76,0.07)' }} />
        <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(245,240,232,0.45)', marginBottom: '0.7rem' }}>
          Financial Health Score
        </div>

        {/* Gauge */}
        <div style={{ width: '150px', margin: '0 auto 0.8rem' }}>
          <svg viewBox="0 0 180 100" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%' }}>
            <path d="M20 90 A70 70 0 0 1 160 90" fill="none" stroke="rgba(245,240,232,0.1)" strokeWidth="12" strokeLinecap="round" />
            <path
              d="M20 90 A70 70 0 0 1 160 90"
              fill="none"
              stroke={score >= 85 ? '#4a6741' : score >= 70 ? '#c9a84c' : score >= 55 ? '#b8441a' : '#c0392b'}
              strokeWidth="12"
              strokeLinecap="round"
              strokeDasharray="220"
              strokeDashoffset={score ? 220 - (score / 100) * 220 : 220}
              style={{ transition: 'stroke-dashoffset 1.1s ease' }}
            />
            <text x="90" y="85" textAnchor="middle" fontFamily="Georgia,serif" fontSize="28" fontWeight="900" fill="#e8c96a">
              {score || '--'}
            </text>
          </svg>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.8rem', marginBottom: '0.8rem' }}>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: '3.5rem', fontWeight: '900', lineHeight: 1, color: '#e8c96a' }}>
            {score || '--'}
          </div>
          <div style={{ paddingBottom: '0.3rem' }}>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.3rem', fontWeight: '700', color: '#f5f0e8' }}>
              {!score ? 'Add Loans' : score >= 85 ? 'Excellent' : score >= 70 ? 'Good' : score >= 55 ? 'Fair' : 'Needs Work'}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'rgba(245,240,232,0.45)' }}>
              {!score ? 'to calculate score' : score >= 85 ? 'Strong financial discipline' : score >= 70 ? 'On track, room to improve' : score >= 55 ? 'Some risks to address' : 'Action recommended'}
            </div>
          </div>
        </div>
        <div style={{ fontSize: '0.72rem', color: 'rgba(245,240,232,0.35)', lineHeight: 1.6, borderTop: '1px solid rgba(245,240,232,0.1)', paddingTop: '0.7rem' }}>
          Factors: interest burden, credit card debt, payoff progress &amp; EMI consistency.
        </div>
      </div>

      {/* SMART SUGGESTIONS */}
      <div style={panelStyle}>
        <div style={hdrStyle}>
          <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Smart Suggestions</span>
          <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', background: 'rgba(201,168,76,0.12)', color: '#7a5d1e', padding: '0.15rem 0.4rem', borderRadius: '3px' }}>✦ AI</span>
        </div>
        {suggestions.length === 0 ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
            Add loans to get personalized suggestions.
          </div>
        ) : (
          suggestions.map((s, i) => (
            <div key={i} style={{
              display: 'flex', gap: '0.75rem', alignItems: 'flex-start',
              padding: '0.9rem 1.2rem',
              borderBottom: i < suggestions.length - 1 ? '1px solid var(--border)' : 'none',
              transition: 'background 0.15s'
            }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.04)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: clsColors[s.cls] || clsColors.info, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.82rem', flexShrink: 0 }}>
                {s.icon}
              </div>
              <div>
                <div style={{ fontWeight: '500', fontSize: '0.82rem', marginBottom: '0.15rem' }}>{s.title}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>{s.text}</div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* PAYOFF STRATEGIES */}
      {loans.length >= 2 && (
        <div style={panelStyle}>
          <div style={hdrStyle}>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Payoff Strategies</span>
            {!isPaid && (
              <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', background: 'rgba(201,168,76,0.12)', color: '#7a5d1e', padding: '0.15rem 0.4rem', borderRadius: '3px' }}>🔒 Premium</span>
            )}
          </div>

          {isPaid ? (
            <>
              <div style={{ padding: '1.1rem 1.4rem', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>🔥 Avalanche Method</div>
                <div style={{ fontSize: '0.8rem', lineHeight: 1.5 }}>
                  {(() => {
                    const top = [...loans].sort((a, b) => b.interest_rate - a.interest_rate)[0]
                    return `Target: ${top.name} (${pct(top.interest_rate)}, ${fmt(top.balance)} left). Pay minimums on all others, direct every extra rupee here.`
                  })()}
                </div>
              </div>
              <div style={{ padding: '1.1rem 1.4rem' }}>
                <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>⚡ Snowball Method</div>
                <div style={{ fontSize: '0.8rem', lineHeight: 1.5 }}>
                  {(() => {
                    const small = [...loans].sort((a, b) => a.balance - b.balance)[0]
                    return `Target: ${small.name} (${fmt(small.balance)} balance). Pay it off fast, then roll that EMI to the next loan.`
                  })()}
                </div>
              </div>
            </>
          ) : (
            /* Locked state for free users */
            <div style={{ position: 'relative', overflow: 'hidden' }}>
              {/* Blurred preview */}
              <div style={{ padding: '1.1rem 1.4rem', borderBottom: '1px solid var(--border)', filter: 'blur(4px)', userSelect: 'none', pointerEvents: 'none', opacity: 0.5 }}>
                <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>🔥 Avalanche Method</div>
                <div style={{ fontSize: '0.8rem', lineHeight: 1.5 }}>Target your highest interest loan first to minimise total interest paid over time.</div>
              </div>
              <div style={{ padding: '1.1rem 1.4rem', filter: 'blur(4px)', userSelect: 'none', pointerEvents: 'none', opacity: 0.5 }}>
                <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>⚡ Snowball Method</div>
                <div style={{ fontSize: '0.8rem', lineHeight: 1.5 }}>Pay off your smallest loan first for quick wins and motivation to keep going.</div>
              </div>
              {/* Lock overlay */}
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(var(--surface-rgb, 255,255,255), 0.7)', backdropFilter: 'blur(1px)', gap: '0.5rem' }}>
                <div style={{ fontSize: '1.5rem' }}>🔒</div>
                <div style={{ fontFamily: 'Georgia,serif', fontSize: '0.9rem', fontWeight: '700', color: 'var(--text)' }}>Premium Feature</div>
                <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)', textAlign: 'center', maxWidth: '220px', lineHeight: 1.5 }}>
                  Upgrade to see your personalised Avalanche & Snowball payoff plans
                </div>
                <button
                  onClick={() => onFeatureLock?.('Payoff Strategies', 'Get personalised Avalanche and Snowball debt payoff plans that show you exactly which loan to target and when — with DebtCrush Premium (₹149/month).')}
                  style={{ marginTop: '0.3rem', background: '#c9a84c', color: '#0d0d0d', border: 'none', padding: '0.4rem 1rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.68rem', fontWeight: '700', cursor: 'pointer' }}
                >
                  Upgrade — ₹149/month
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* SPONSOR PARTNERS */}
      {sponsors.length > 0 && (
        <div style={panelStyle}>
          <div style={hdrStyle}>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Lending Partners</span>
            <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', background: 'rgba(61,74,92,0.1)', color: '#3d4a5c', padding: '0.15rem 0.4rem', borderRadius: '3px' }}>SPONSORED</span>
          </div>
          {sponsors.map((sp, i) => (
            <div key={sp.id} style={{
              padding: '1rem 1.4rem',
              borderBottom: i < sponsors.length - 1 ? '1px solid var(--border)' : 'none',
              transition: 'background 0.15s'
            }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.04)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '0.88rem', marginBottom: '0.2rem' }}>{sp.name}</div>
                  {sp.description && <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginBottom: '0.3rem', lineHeight: 1.4 }}>{sp.description}</div>}
                  {(sp.interest_rate_from || sp.interest_rate_to) && (
                    <div style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#4a6741', marginBottom: '0.3rem' }}>
                      Rate: {sp.interest_rate_from}% – {sp.interest_rate_to}% p.a.
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                    {(sp.loan_types || []).map(t => (
                      <span key={t} style={{ fontFamily: 'monospace', fontSize: '0.6rem', background: 'var(--surface-hdr)', color: '#3d4a5c', padding: '0.1rem 0.4rem', borderRadius: '3px' }}>
                        {t === 'mortgage' ? 'Home Loan' : t === 'auto' ? 'Auto Loan' : t === 'student' ? 'Education' : t === 'personal' ? 'Personal' : 'Credit Card'}
                      </span>
                    ))}
                  </div>
                </div>
                {sp.website_url && (
                  <a
                    href={sp.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      background: '#0d0d0d', color: '#f5f0e8',
                      padding: '0.3rem 0.7rem', borderRadius: '4px',
                      fontFamily: 'monospace', fontSize: '0.65rem',
                      textDecoration: 'none', flexShrink: 0,
                      textTransform: 'uppercase', letterSpacing: '0.05em'
                    }}
                  >
                    Visit →
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  )
}